import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import type { MetaDefinition } from '@angular/platform-browser';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';
import { EnvService } from '../env/env.service';
import { appendPaginationParam, getPaginationPage, normalizeSeoUrl } from './normalize-seo-url';
import type { SeoData } from './seo-data';

@Injectable({
  providedIn: 'root',
})
export class ServerSeoDataService {
  fullUrl: string;

  /**
   * Current paginated page (>= 2) derived from the route's `?page=N` param, or
   * null on page 1 / non-paginated routes. Captured here because normalizeSeoUrl
   * strips the query string — this is the only surviving source of the signal.
   */
  private readonly paginationPage: number | null;

  constructor(
    @Inject(DOCUMENT) private dom,
    private titleService: Title,
    private meta: Meta,
    private bfs: BrandConfigurationService,
    private readonly router: Router,
    private env: EnvService,
  ) {
    // @ts-ignore
    //To be refactored in phase 3 once separate folders are created
    const hostname = 'https://' + this.env.get.brandConfig.url;
    this.paginationPage = getPaginationPage(this.router.url);
    this.fullUrl = normalizeSeoUrl(`${hostname}${this.router.url}`);
  }

  /**
   * Normalize a base URL for SEO output and re-attach the current `?page=N`
   * param on paginated pages. Shared by canonical and og:url so the two tags
   * are byte-identical on paginated listing pages (EPS-4097).
   *
   * Note: if a page ever supplies a Craft canonical pointing to a *different*
   * path than the current route while that route is paginated, the page param
   * is layered onto the foreign canonical. In practice `?page=N` only appears
   * on self-canonical listing pages, so this is an accepted limitation.
   */
  private buildSeoUrl(baseUrl: string): string {
    return appendPaginationParam(normalizeSeoUrl(baseUrl), this.paginationPage);
  }

  private setCanonicalUrl(canonicalUrl: string): void {
    if (canonicalUrl) {
      const link: HTMLLinkElement = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', this.buildSeoUrl(canonicalUrl));
      this.dom.head.appendChild(link);
    }
  }

  private createMetadataArray(seoData: SeoData): MetaDefinition[] {
    if (!seoData) {
      return;
    }
    const { overrideRobotsDirectives } = this.env.get;
    const { description, keywords, ogUrl, ogTitle, ogType, ogImage, noindex, nofollow } = seoData;
    const robots = overrideRobotsDirectives
      ? overrideRobotsDirectives
      : `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`;
    const defaultOgImage = this.env.get.defaultOgImage;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'robots', content: robots },

      // Social verifications
      {
        name: 'facebook-domain-verification',
        content: this.env.get.facebookDomainVerification,
      },
      {
        name: 'google-site-verification',
        content: this.env.get.googleSiteVerification,
      },

      /**
       * Open Graph
       * @see https://ogp.me/
       */
      { property: 'og:description', content: description }, // Not a Craft field
      { property: 'og:locale', content: 'en_US' }, // Not a Craft field
      { property: 'og:site_name', content: this.bfs.name }, // Not a Craft field
      { property: 'og:image', content: ogImage || defaultOgImage },
      { property: 'og:type', content: ogType || 'website' },
      { property: 'og:title', content: ogTitle || seoData.pageTitle },
      { property: 'og:url', content: this.buildSeoUrl(ogUrl || seoData.canonicalUrl || this.fullUrl) },

      /**
       * Twitter
       * @see https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup
       */
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:image', content: ogImage || defaultOgImage },
      { property: 'twitter:site', content: this.env.get.twitterHandle },
    ];

    return metaTags.filter((tag) => tag.content); // Filter for truthy values
  }

  private setMetadata(seoData: SeoData): void {
    this.meta.addTags(this.createMetadataArray(seoData), true);
  }

  private setTitle(pageTitle: string): void {
    this.titleService.setTitle(pageTitle);
  }

  setSeoProperties(seoData: SeoData): void {
    const { overrideCanonical } = this.env.get;

    this.setTitle(seoData?.pageTitle);
    this.setMetadata(seoData);

    // Canonical URL priority:
    // 1. overrideCanonical (if override) - environment override takes precedence
    // 2. seoData?.canonical (if overrideCanonical is not truthy) - use Craft CMS canonical URL
    // 3. this.fullUrl (if both above are not truthy ) - fallback to constructed current page URL
    this.setCanonicalUrl(overrideCanonical || seoData?.canonicalUrl || this.fullUrl);
  }
}
